// @stayai — generic, configurable selling-plan <option> hider for plain-DOM buyboxes/dropdowns.
//
// For light DOM only. Not for vendors that render inside a shadow root 
//
// Configure via window.STAYAI_SELLING_PLAN_HIDER_CONFIGS — an array, one entry per distinct
// widget/dropdown on this store (a single object is also accepted). Each entry:
//
//   widgetSelector   {String}   required. Root element to scope detection/observation to.
//   dropdownSelector {String}   required. CSS selector for the <select> to filter, evaluated
//                                within widgetSelector's subtree.
//   excludeSelector  {String}   optional. A matched <select> is skipped if
//                                select.closest(excludeSelector) is non-null. Use when
//                                dropdownSelector is broad and could accidentally match unrelated
//                                controls (e.g. a Stay AI self-service buybox that is already
//                                self-gated in Liquid, or a cart-drawer filter managed by a
//                                separate dedicated script).
//   activeAppId      {String}   optional. Falls back to window.STAYAI_ACTIVE_APP_ID.
//   hideTerms        {String[]} optional. Case-insensitive substring match against a plan's own
//                                name, its options[].name/options[].value, and its owning group's
//                                name — any match hides that option regardless of app_id.
//                                Default: [].
//   plansDataGlobal  {String}   optional. Name of a window global holding
//                                product.selling_plan_groups. Must be synchronous (Liquid-emitted)
//                                — never an async fetch, which arrives too late to prevent a flash
//                                of unfiltered options. Default: 'ALL_SELLING_PLAN_GROUPS'.
//   customLabel      {Object}   optional. { wrapperSelector, labelSelector } — set only if the
//                                vendor mirrors the <select>'s value into a separately-styled
//                                label element that doesn't auto-update from a native `change`
//                                event.
//   debugFlag        {String}   optional. Name of a window global; when truthy, enables
//                                '>>> '-prefixed console logging. Default:
//                                'STAYAI_SELLING_PLAN_HIDER_DEBUG'.
//
// At least one of activeAppId/window.STAYAI_ACTIVE_APP_ID or hideTerms must resolve to something,
// or a config entry is a no-op by design (nothing to filter by).
//
// Prerequisite: the theme must emit product.selling_plan_groups synchronously wherever this
// widget renders, e.g.:
//   <script>window.ALL_SELLING_PLAN_GROUPS = {{ product.selling_plan_groups | json }};</script>
(function () {
  var configs = window.STAYAI_SELLING_PLAN_HIDER_CONFIGS || window.STAYAI_SELLING_PLAN_HIDER_CONFIG;
  if (!configs) return;
  if (!Array.isArray(configs)) configs = [configs];

  configs.forEach(runWidget);

  function runWidget(config) {
    var ACTIVE_APP_ID = String(config.activeAppId || window.STAYAI_ACTIVE_APP_ID || '').trim();
    var HIDE_TERMS = (config.hideTerms || []).map(function (t) { return String(t).toLowerCase(); });
    var PLANS_GLOBAL = config.plansDataGlobal || 'ALL_SELLING_PLAN_GROUPS';
    var DEBUG_FLAG = config.debugFlag || 'STAYAI_SELLING_PLAN_HIDER_DEBUG';
    var DEBUG = window[DEBUG_FLAG] || false;

    if (!ACTIVE_APP_ID && !HIDE_TERMS.length) return; // nothing to filter by — no-op by design

    function log() {
      if (DEBUG) {
        console.log.apply(console, ['>>> [selling-plan-hider:' + config.widgetSelector + ']'].concat(Array.prototype.slice.call(arguments)));
      }
    }

    function matchesHiddenTerm(text) {
      var lower = String(text || '').toLowerCase();
      return HIDE_TERMS.some(function (term) {
        return lower.indexOf(term) !== -1;
      });
    }

    function buildMaps() {
      var groups = window[PLANS_GLOBAL] || [];
      var appIdMap = {};
      var hideByTermMap = {};
      groups.forEach(function (g) {
        (g.selling_plans || []).forEach(function (p) {
          appIdMap[String(p.id)] = g.app_id;

          var searchable = [g.name, p.name];
          (p.options || []).forEach(function (o) {
            searchable.push(o.name);
            searchable.push(o.value);
          });
          if (matchesHiddenTerm(searchable.join(' '))) {
            hideByTermMap[String(p.id)] = true;
          }
        });
      });
      return { appIdMap: appIdMap, hideByTermMap: hideByTermMap };
    }

    function filterSelect(select, maps) {
      // A confirmed-irrelevant select (no recognized plan-id options) is safe to skip permanently.
      if (select.dataset.stayIrrelevant) return;
      // Skip untouched unless at least one option is a recognized plan id — a broad
      // dropdownSelector could otherwise match unrelated controls and accidentally trigger the
      // change-dispatch branch below on a widget that was never in scope.
      var hasKnownOption = Array.prototype.some.call(select.options, function (opt) {
        return maps.appIdMap[opt.value] !== undefined || maps.hideByTermMap[opt.value] === true;
      });
      if (!hasKnownOption) {
        select.dataset.stayIrrelevant = 'true';
        return;
      }
      // Not gated on "already filtered once" — a reactive framework can reconcile the same
      // <select> node in place and restore hidden options without touching our dataset marker.
      // Re-checking is cheap and re-applying disabled/hidden to an already-hidden option is a
      // no-op, so continuously re-applying on each mutation is safe.
      var selectedForeign = false;
      Array.prototype.slice.call(select.options).forEach(function (opt) {
        var appId = maps.appIdMap[opt.value];
        var hiddenByTerm = maps.hideByTermMap[opt.value] === true;
        var isForeign = !!ACTIVE_APP_ID && appId !== undefined && appId !== ACTIVE_APP_ID;
        var shouldHide = isForeign || hiddenByTerm;
        // Never opt.remove() — removing a node from under a reactive framework's virtual DOM
        // causes a removeChild crash on the next reconciliation. disabled+hidden is an attribute
        // mutation (not a tree mutation), so the node stays exactly where the virtual DOM expects
        // it and reconciliation continues safely.
        opt.disabled = shouldHide;
        opt.hidden = shouldHide;
        if (shouldHide) {
          if (opt.selected) selectedForeign = true;
          if (hiddenByTerm) log('hiding term-matched plan', opt.value, opt.textContent);
        }
      });
      if (selectedForeign || !select.value) {
        var fallback = select.querySelector('option:not([disabled])');
        if (fallback) {
          select.value = fallback.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          if (config.customLabel) {
            var wrapper = select.closest(config.customLabel.wrapperSelector);
            var label = wrapper && wrapper.querySelector(config.customLabel.labelSelector);
            if (label) label.textContent = fallback.dataset.sellingPlanName || fallback.textContent;
          }
        }
      }
      select.dataset.stayFiltered = 'true';
      log('filtered', select);
    }

    function scan(root, maps) {
      root.querySelectorAll(config.dropdownSelector).forEach(function (select) {
        if (config.excludeSelector && select.closest(config.excludeSelector)) return;
        filterSelect(select, maps);
      });
    }

    function init() {
      var root = document.querySelector(config.widgetSelector);
      if (!root) return;
      var maps = buildMaps();
      if (!Object.keys(maps.appIdMap).length && !Object.keys(maps.hideByTermMap).length) {
        log('no plan data / no matches found on window.' + PLANS_GLOBAL + ' — skipping');
        return;
      }
      scan(root, maps);
      // Debounced: prevents excessive calls on high-churn pages where mutations fire rapidly.
      var debounce;
      new MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(function () { scan(root, maps); }, 30);
      }).observe(root, { childList: true, subtree: true });
    }

    function waitForRoot(attempts) {
      var root = document.querySelector(config.widgetSelector);
      if (root) return init();
      if (attempts <= 0) return log('widget root "' + config.widgetSelector + '" never appeared — exiting');
      setTimeout(function () { waitForRoot(attempts - 1); }, 500);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { waitForRoot(40); });
    } else {
      waitForRoot(40);
    }
  }
})();
